import { arrayOf, shape, string } from '@thd-nucleus/data-sources';
import { ExperienceMapper } from '@ninetailed/experience.js-utils';

export const getNinetailedDataModel = (componentDataModel) => arrayOf(
  shape({
    nt_experience_id: string(),
    nt_name: string(),
    nt_description: string(),
    nt_type: string(),
    nt_config: string(),
    nt_audience: shape({
      componentName: string(),
      nt_name: string(),
      nt_description: string(),
      nt_rules: string(),
      nt_audience_id: string(),
      nt_metadata: string(),
    }),
    nt_variants: arrayOf(
      shape(
        componentDataModel
      )
    ),
  }));

export const getExperienceMap = (experiences) => {
  return experiences
    .map((experience) => {
      return {
        id: experience.nt_experience_id,
        name: experience.nt_name,
        type: experience.nt_type,
        config: JSON.parse(experience.nt_config),
        audience: {
          id: experience.nt_audience.nt_audience_id, // Required
          name: experience.nt_audience.nt_name, // Required
        },
        variants: experience.nt_variants.map((variant) => {
          return {
            ...variant,
            componentId: variant.id
          };
        })
      };
    })
    .filter((experience) => {
      return ExperienceMapper.isExperienceEntry(experience);
    })
    .map((experience) => {
      return ExperienceMapper.mapExperience(experience);
    });
};

export const getNinetailedExperience = (experiences) => {
  if (!experiences || !Array.isArray(experiences)) {
    return [];
  }
  return getExperienceMap(experiences);
};

export const fetchNinetailedComponentExperiencesFormatted = async (
  slug,
  CONTENTFUL_PREVIEW_TOKEN,
  CONTENTFUL_SPACE_ID,
  CONTENTFUL_ENV
) => {
  // Get the page with all its descendants (many generations deep)
  const domain = 'https://preview.contentful.com';
  const path = `/spaces/${CONTENTFUL_SPACE_ID}/environments/${CONTENTFUL_ENV}/entries`;
  const queryParams = `?content_type=page&fields.slug=${slug}&include=10`;
  const pageUrl = `${domain}${path}${queryParams}`;
  const pageResponse = await fetch(pageUrl, {
    headers: { Authorization: `Bearer ${CONTENTFUL_PREVIEW_TOKEN}` },
  });

  if (!pageResponse.ok) {
    throw new Error('Failed to fetch page from Contentful');
  }

  const pageData = await pageResponse.json();

  if (!pageData.items?.length) {
    throw new Error(`No page found with slug: ${slug}`);
  }

  // Filter descendants by content type = nt_experience
  const experiments = pageData.includes?.Entry?.filter((entry) => {
    return entry.sys.contentType?.sys.id === 'nt_experience';
  }
  ) || [];

  if (experiments.length === 0) {
    return [];
  }

  // Get experiment IDs and fetch with audiences
  const experimentIds = experiments.map((exp) => exp.sys.id);
  const experimentQueryParams = `?sys.id[in]=${experimentIds.join(',')}&content_type=nt_experience&include=2`;
  const experimentsWithAudiencesUrl = `${domain}${path}${experimentQueryParams}`;

  const experimentsWithAudiencesResponse = await fetch(experimentsWithAudiencesUrl, {
    headers: { Authorization: `Bearer ${CONTENTFUL_PREVIEW_TOKEN}` },
  });

  if (!experimentsWithAudiencesResponse.ok) {
    throw new Error('Failed to fetch experiments with audiences');
  }

  const experimentsWithAudiencesData = await experimentsWithAudiencesResponse.json();

  // Process and return the experiments
  const experiences = experimentsWithAudiencesData.items
    .map((experience) => {
      const audienceId = experience.fields.nt_audience?.sys?.id;
      const audienceFields = audienceId
        ? experimentsWithAudiencesData.includes?.Entry?.find((entry) => entry.sys.id === audienceId)?.fields
        : null;

      return {
        id: experience.fields.nt_experience_id,
        name: experience.fields.nt_name,
        type: experience.fields.nt_type,
        config: experience.fields.nt_config,
        audience: audienceFields ? {
          id: audienceFields.nt_audience_id,
          name: audienceFields.nt_name,
          metadata: audienceFields.nt_metadata,
          rules: audienceFields.nt_rules,
        } : experience.fields.nt_audience,
        variants: experience.fields.nt_variants?.map((variant) => ({
          id: variant.sys.id,
          dataComponent: variant,
          cmMmcParam: '',
          componentId: variant.sys.id,
          componentClass: variant.componentName,
          componentPosition: 1,
          hasSidenav: false,
          ...variant,
        })) || []
      };
    })
    .filter((experience) => ExperienceMapper.isExperienceEntry(experience))
    .map((experience) => ExperienceMapper.mapExperience(experience));

  return experiences;
};
